import {
  createRouter,
  createWebHistory,
  type RouteLocationNormalized,
  type RouteLocationRaw,
  type RouteRecordRaw,
} from 'vue-router';
import { DEV_SESSION_ENABLED } from '@/utils/devSession';
import { ApiRequestError } from '@/api/client';
import { PERM, type Permission } from '@kitobxonlik/contracts';
import { useSessionStore } from '@/stores/session.store';

declare module 'vue-router' {
  interface RouteMeta {
    /** Sessiya talab qilinmaydi */
    public?: boolean;
    /**
     * Kerakli RUXSATLAR — kamida bittasi bo'lishi shart (OR).
     * Rol nomi ATAYLAB yo'q: rolga qaysi ruxsat tegishli — bazada.
     */
    permissions?: Permission[];
    /** Layout: 'app' (default) | 'exam' | 'reader' | 'bare' */
    layout?: 'app' | 'exam' | 'reader' | 'bare';
    title?: string;
  }
}

const routes: RouteRecordRaw[] = [
  /**
   * Ildiz — bu yerda `redirect` ATAYLAB YO'Q.
   *
   * Marshrutdagi `redirect` funksiyasi `beforeEach` dan OLDIN, ya'ni sessiya
   * tiklanishidan oldin ishlaydi. Shuning uchun sovuq yuklashda (foydalanuvchi
   * "/" ni to'g'ridan-to'g'ri ochganda) store bo'sh bo'lib, oddiy xodim ham
   * Statistikaga yuborilar va "Ruxsat yo'q" sahifasiga tushardi. HR'dan
   * o'tishda (`?p=`) ham shu sabab noto'g'ri manzilga borardi.
   *
   * Endi qaror `beforeEach` ichida, sessiya aniq bo'lgandan keyin qabul
   * qilinadi (`landingRoute`).
   */
  {
    path: '/',
    name: 'root',
    component: () => import('@/views/RootView.vue'),
    meta: { layout: 'bare' },
  },

  // --- Xodim ---
  {
    path: '/home',
    name: 'home',
    component: () => import('@/views/home/HomeView.vue'),
    meta: { permissions: [PERM.LIBRARY_READ], title: 'Asosiy' },
  },
  {
    path: '/library',
    name: 'library',
    component: () => import('@/views/library/LibraryView.vue'),
    meta: { permissions: [PERM.LIBRARY_READ], title: 'Kutubxona' },
  },
  {
    path: '/library/:id(\\d+)',
    name: 'book',
    component: () => import('@/views/library/BookView.vue'),
    meta: { permissions: [PERM.LIBRARY_READ], title: 'Kitob' },
  },
  {
    path: '/library/:id(\\d+)/read',
    name: 'book-read',
    component: () => import('@/views/library/ReaderView.vue'),
    meta: { permissions: [PERM.LIBRARY_READ], layout: 'reader', title: 'Öqiş' },
  },
  {
    path: '/calendar',
    name: 'calendar',
    component: () => import('@/views/calendar/CalendarView.vue'),
    meta: { permissions: [PERM.LIBRARY_READ], title: 'Kalendar' },
  },
  {
    path: '/my-stats',
    name: 'my-stats',
    component: () => import('@/views/me/MyStatsView.vue'),
    meta: { permissions: [PERM.LIBRARY_READ], title: 'Körsatkiçlarim' },
  },
  {
    path: '/exams',
    name: 'exams',
    component: () => import('@/views/exams/MyExamsView.vue'),
    meta: { permissions: [PERM.EXAM_TAKE], title: 'Imtihonlarim' },
  },
  {
    path: '/exams/:id(\\d+)/result',
    name: 'exam-result',
    component: () => import('@/views/exams/ExamResultView.vue'),
    meta: { permissions: [PERM.EXAM_TAKE], title: 'Natija' },
  },
  {
    path: '/exam/:bookId(\\d+)',
    name: 'exam-room',
    component: () => import('@/views/exams/ExamRoomView.vue'),
    meta: { permissions: [PERM.EXAM_TAKE], layout: 'exam', title: 'Imtihon' },
  },

  // --- Boshqaruv ---
  { path: '/admin', redirect: { name: 'admin-statistics' } },
  {
    path: '/admin/statistics',
    name: 'admin-statistics',
    component: () => import('@/views/admin/StatisticsView.vue'),
    meta: { permissions: [PERM.STATISTICS_READ], title: 'Statistika' },
  },
  // Eski manzil (bookmark/havolalar buzilmasin)
  { path: '/admin/dashboard', redirect: { name: 'admin-statistics' } },
  {
    path: '/admin/books',
    name: 'admin-books',
    component: () => import('@/views/admin/AdminBooksView.vue'),
    meta: { permissions: [PERM.BOOK_READ], title: 'Kitoblar' },
  },
  {
    path: '/admin/books/new',
    name: 'admin-book-new',
    component: () => import('@/views/admin/BookFormView.vue'),
    meta: { permissions: [PERM.BOOK_WRITE], title: 'Yangi kitob' },
  },
  {
    path: '/admin/books/:id(\\d+)/edit',
    name: 'admin-book-edit',
    component: () => import('@/views/admin/BookFormView.vue'),
    meta: { permissions: [PERM.BOOK_WRITE], title: 'Kitobni tahrirlaş' },
  },
  {
    path: '/admin/books/:id(\\d+)/questions',
    name: 'admin-questions',
    component: () => import('@/views/admin/QuestionsView.vue'),
    meta: { permissions: [PERM.QUESTION_READ], title: 'Savollar' },
  },
  {
    path: '/admin/exams',
    name: 'admin-exams',
    component: () => import('@/views/admin/AdminExamsView.vue'),
    meta: { permissions: [PERM.EXAM_READ], title: 'Imtihonlar' },
  },
  {
    path: '/admin/exams/:id(\\d+)',
    name: 'admin-exam',
    component: () => import('@/views/admin/AdminExamView.vue'),
    meta: { permissions: [PERM.EXAM_READ], title: 'Imtihon' },
  },
  {
    path: '/admin/calendar',
    name: 'admin-calendar',
    component: () => import('@/views/calendar/CalendarView.vue'),
    props: { admin: true },
    meta: { permissions: [PERM.EXAM_READ], title: 'Kalendar' },
  },
  {
    path: '/admin/organizations',
    name: 'admin-organizations',
    component: () => import('@/views/admin/OrganizationsView.vue'),
    meta: { permissions: [PERM.ORGANIZATION_READ], title: 'Taşkilotlar' },
  },
  {
    path: '/admin/organizations/:id(\\d+)',
    name: 'admin-organization',
    component: () => import('@/views/admin/OrganizationView.vue'),
    meta: { permissions: [PERM.ORGANIZATION_READ], title: 'Taşkilot' },
  },
  {
    path: '/admin/dictionaries',
    name: 'admin-dictionaries',
    component: () => import('@/views/admin/DictionariesView.vue'),
    meta: { permissions: [PERM.DICTIONARY_READ], title: 'Luğatlar' },
  },
  {
    path: '/admin/roles',
    name: 'admin-roles',
    component: () => import('@/views/admin/RolesView.vue'),
    meta: { permissions: [PERM.ROLE_READ], title: 'Rollar va ruxsatlar' },
  },
  {
    path: '/admin/users',
    name: 'admin-users',
    component: () => import('@/views/admin/UsersView.vue'),
    meta: { permissions: [PERM.USER_READ], title: 'Foydalanuvçilar' },
  },

  // --- Sessiya ---
  {
    /**
     * OneID callback manzili — SSO shu yerga `?code=` bilan qaytaradi.
     * Yo'l `SSO_REDIRECT_URIS` va SSO klient sozlamasi bilan MOS bo'lishi shart.
     */
    path: '/login',
    name: 'login',
    component: () => import('@/views/auth/LoginView.vue'),
    meta: { public: true, layout: 'bare', title: 'Kiriş' },
  },
  {
    path: '/no-session',
    name: 'no-session',
    component: () => import('@/views/NoSessionView.vue'),
    meta: { public: true, layout: 'bare', title: 'Kiriş' },
  },
  {
    path: '/forbidden',
    name: 'forbidden',
    component: () => import('@/views/ForbiddenView.vue'),
    meta: { layout: 'bare', title: 'Ruxsat yöq' },
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'not-found',
    component: () => import('@/views/NotFoundView.vue'),
    meta: { public: true, layout: 'bare', title: 'Topilmadi' },
  },
];

if (DEV_SESSION_ENABLED) {
  routes.push({
    path: '/dev/session',
    name: 'dev-session',
    component: () => import('@/views/dev/DevSessionView.vue'),
    meta: { public: true, layout: 'bare', title: 'Dev sessiya' },
  });
}

export const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior: () => ({ top: 0 }),
});

/**
 * Foydalanuvchi ochishga HAQLI birinchi sahifa.
 *
 * Tartib menyu tartibi bilan bir xil: xodim sahifalari, keyin boshqaruv.
 * Hech qanday ruxsat bo'lmasa "Ruxsat yo'q" — bu haqiqatan ham shunday
 * holat (rol biriktirilmagan foydalanuvchi).
 */
function landingRoute(session: ReturnType<typeof useSessionStore>): RouteLocationRaw {
  if (session.can(PERM.LIBRARY_READ)) return { name: 'home' };
  if (session.can(PERM.EXAM_TAKE)) return { name: 'exams' };
  if (session.can(PERM.STATISTICS_READ)) return { name: 'admin-statistics' };
  if (session.can(PERM.BOOK_READ)) return { name: 'admin-books' };
  if (session.can(PERM.EXAM_READ)) return { name: 'admin-exams' };
  if (session.can(PERM.ORGANIZATION_READ)) return { name: 'admin-organizations' };
  if (session.can(PERM.DICTIONARY_READ)) return { name: 'admin-dictionaries' };
  if (session.can(PERM.ROLE_READ)) return { name: 'admin-roles' };
  if (session.can(PERM.USER_READ)) return { name: 'admin-users' };
  return { name: 'forbidden' };
}

/**
 * HR portalidan o'tish: `?p=<shifrlangan PINFL>`.
 *
 * HR'dagi "Kitobxonlik" tugmasi istalgan sahifaga shunday link bilan yuborishi
 * mumkin, shuning uchun bu ILDIZ qo'riqchida — alohida sahifa emas. Muvaffaqiyat
 * bo'lsa `p` manzildan olib tashlanadi: brauzer tarixida va ulashilgan linkda
 * shifr qolmaydi.
 */
async function tryHrEntry(to: RouteLocationNormalized): Promise<RouteLocationRaw | null> {
  const p = to.query.p;
  if (typeof p !== 'string' || !p) return null;

  const session = useSessionStore();
  const { p: _p, ...restQuery } = to.query;

  try {
    await session.loginWithHrEntry(p);

    // Ildizga kelgan bo'lsa — ruxsatga mos sahifaga; aks holda o'sha sahifaga
    return to.name === 'root' || to.path === '/'
      ? { ...(landingRoute(session) as object), replace: true }
      : { path: to.path, query: restQuery, replace: true };
  } catch (err) {
    /*
     * Havola eskirgan/yaroqsiz (401) — «havola eskirgan» matni. Boshqa sabab
     * (HR'da xodim emas — 403, HR javob bermadi — 500, hisob bloklangan) server
     * xabari bilan ko'rsatiladi: hammasini «eskirgan» deyish foydalanuvchini
     * qayta-qayta urinishga undab, haqiqiy sababni yashirardi.
     */
    session.entryError = err instanceof ApiRequestError && err.status !== 401 ? err.message : null;
    return { name: 'no-session', query: { reason: 'hr-entry' }, replace: true };
  }
}

/**
 * Qo'riqchi: sessiya `GET /me` orqali bir marta tiklanadi. Bu faqat UX —
 * haqiqiy himoya serverdagi guard'larda.
 */
router.beforeEach(async (to) => {
  const session = useSessionStore();

  // HR'dan o'tish sessiyani tiklashdan OLDIN: `?p=` bo'lsa yangi sessiya ochamiz
  const handoff = await tryHrEntry(to);
  if (handoff) return handoff;

  /*
   * `/login` da sessiya TIKLANMAYDI.
   *
   * Bu sahifa — OneID kirish nuqtasi va uning callback'i (`?code=`). Ikkala
   * holatda ham cookie hali YO'Q: `restore()` `GET /me` ni 401 bilan oldirardi,
   * interceptor esa ustiga yana `POST /v1/auth/refresh` yuborib, u ham 401
   * bo'lardi. Ya'ni har kirishda ikkita behuda 401 (tarmoq panelida "xato"
   * bo'lib ko'rinardi). Sessiyani `LoginView` ning o'zi `loginWithOneId()`
   * orqali ochadi va `apply()` uni `initialized` qilib belgilaydi.
   */
  if (to.name !== 'login') await session.restore();

  if (to.meta.public) return true;

  if (!session.isAuthenticated) {
    return { name: 'no-session', query: { redirect: to.fullPath } };
  }

  // Ildiz — sessiya aniq bo'lgandan KEYIN hal qilinadi
  if (to.name === 'root') return landingRoute(session);

  if (to.meta.permissions && !session.can(...to.meta.permissions)) {
    return { name: 'forbidden' };
  }

  return true;
});

router.afterEach((to) => {
  document.title = to.meta.title ? `${to.meta.title} — Kitobxonlik` : 'Kitobxonlik';
});
